import React from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ExternalLink } from "react-feather";
import SectionCard from "../../../../components/common/SectionCard";
import { money } from "../../../dashboard/dashboardHelpers";
import { moduleMeta } from "../oneStopModules";
import { drillTarget } from "../oneStopDrill";

const fmtWhen = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return `${d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })} ${d
    .toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`;
};

const fmtDateOnly = (iso) => {
  if (!iso) return "never";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

/**
 * The employee's linked user account, and what that account actually did.
 *
 * The caveat box is not decoration: there is no login-history table anywhere in
 * this database (App_Users.LastLoginDate is one value, overwritten every login),
 * so this trail is rebuilt from the CreatedBy/ApprovedBy columns already on each
 * module's tables. That makes it work retroactively over all existing data, but
 * it can never show logins - saying so on the page is the difference between a
 * report someone can trust and one they quietly misread.
 */
const UserActivityPanel = ({ identity, activity = [] }) => {
  if (!identity || identity.masterType !== "EMPLOYEE") return null;

  if (!identity.linkedUserId) {
    return (
      <SectionCard icon={AlertTriangle} title="Linked user account">
        <p className="mb-0 text-muted" style={{ fontSize: 13 }}>
          This employee has no user account linked to them (<code>App_Users.EmployeeId</code> is not
          set for anyone matching their RecordGuid), so there is no activity trail to show. Their
          transactions above are still complete - those come from the module tables directly, not
          from a user account.
        </p>
      </SectionCard>
    );
  }

  return (
    <SectionCard
      icon={ExternalLink}
      title="Linked user account & activity trail"
      right={<span className="badge bg-light text-secondary border fw-normal">{identity.linkedUsername}</span>}
    >
      <div className="row g-3">
        <div className="col-12 col-lg-4">
          <dl className="row mb-0" style={{ fontSize: 12.5 }}>
            <dt className="col-5 text-muted fw-normal">Username</dt>
            <dd className="col-7 fw-semibold">{identity.linkedUsername}</dd>
            <dt className="col-5 text-muted fw-normal">Status</dt>
            <dd className="col-7 fw-semibold">
              <span
                className={
                  identity.linkedUserIsActive ? "text-success" : "text-secondary"
                }
              >
                {identity.linkedUserIsActive ? "Active" : "Inactive"}
              </span>
            </dd>
            <dt className="col-5 text-muted fw-normal">Designation</dt>
            <dd className="col-7 fw-semibold">{identity.linkedUserDesignation || "—"}</dd>
            <dt className="col-5 text-muted fw-normal">Last login</dt>
            <dd className="col-7 fw-semibold">{fmtDateOnly(identity.linkedUserLastLogin)}</dd>
            <dt className="col-5 text-muted fw-normal">Actions in period</dt>
            <dd className="col-7 fw-semibold">{activity.length}</dd>
          </dl>

          <div
            className="mt-3 p-2 rounded"
            style={{ border: "1px solid rgba(169,117,10,.5)", borderLeftWidth: 3, fontSize: 12 }}
          >
            <strong className="text-warning">No login history exists.</strong>{" "}
            <code>App_Users.LastLoginDate</code> holds only the most recent login and is overwritten
            each time, and no login-audit table exists. The trail here is rebuilt from the{" "}
            <code>CreatedBy</code> / <code>ApprovedBy</code> columns already on each module&rsquo;s
            tables, so it covers all historic data - but it shows actions, not logins.
          </div>
        </div>

        <div className="col-12 col-lg-8">
          {activity.length === 0 ? (
            <p className="text-muted mb-0" style={{ fontSize: 13 }}>
              This account created or approved nothing in the selected period.
            </p>
          ) : (
            <div className="onestop-txscroll" style={{ paddingLeft: 0 }}>
              <table className="onestop-tx">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Module</th>
                    <th>Action</th>
                    <th>Document</th>
                    <th>Branch</th>
                    <th style={{ textAlign: "right" }}>Value</th>
                    <th>Drill down</th>
                  </tr>
                </thead>
                <tbody>
                  {activity.map((a, i) => {
                    const meta = moduleMeta(a.moduleCode);
                    const target = drillTarget(a);
                    return (
                      <tr key={`${a.drillGuid || a.documentNo || "a"}-${i}`}>
                        <td className="onestop-num">{fmtWhen(a.actedDate)}</td>
                        <td>
                          <span
                            className="badge bg-light fw-normal border"
                            style={{ color: meta.light, borderColor: `${meta.light}66` }}
                          >
                            {a.moduleName}
                          </span>
                        </td>
                        <td className="text-body-secondary">{a.action}</td>
                        <td className="onestop-num">{a.documentNo || "—"}</td>
                        <td className="text-body-secondary">{a.branchCode}</td>
                        <td className="onestop-money">
                          {a.amount == null ? (
                            <span className="text-muted fw-normal">&mdash;</span>
                          ) : (
                            money(a.amount)
                          )}
                        </td>
                        <td>
                          {target ? (
                            <Link to={target} className="d-inline-flex align-items-center gap-1">
                              <ExternalLink size={12} />
                              Open
                            </Link>
                          ) : (
                            <span className="text-muted" style={{ fontSize: 11 }}>
                              no screen yet
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </SectionCard>
  );
};

export default UserActivityPanel;
